import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { apiEndpoints, httpMethods } from "../../constants/api.constants";
import { loaderPath } from "../../descriptions/manufacturer.description";
import useAPI from "../../hooks/useAPI";
import useI18N from "../../hooks/useI18N";
import { Eye } from "../../shared/icon";
import MUIBreadcrumbs from "../../shared/MUIBreadcrumbs";
import MUIModal from "../../shared/MUIModal";
import { MUIStyled } from "../../shared/MUIStyled";
import SABox from "../../shared/SABox";
import SACard from "../../shared/SACard";
import SAImageSlider from "../../shared/SAContentSlider";
import SALoader from "../../shared/SALoader";
import SAStack from "../../shared/SAStack";
import SAStatusBadge from "../../shared/SAStatusBadge";
import SATruncateWithEllipsis from "../../shared/SATruncateWithEllipsis";
import SATypography from "../../shared/SATypography";
import SACost from "../../shared/table-components/SACost";
import SADate from "../../shared/table-components/SADate";
import JewelerSerialNumberTable from "../jewellery-inspector/manufacture-requests/JewelerSerialNumberTable";
import ProductMaterialsTable from "../musharka-contracts/ProductMaterialsTable";
import ManufactureProductDetailsModal from "./ManufactureProductDetailsModal";

const DetailsRow = MUIStyled(SABox)({
  display: "flex",
  alignItems: "center",
  background: "#f9fafb",
  borderRadius: 2,
  padding: 8,
  marginBottom: 8,
  gap: 8,
});

const DetailsToggle = MUIStyled(SABox)({
  textTransform: "none",
  fontWeight: 600,
  paddingLeft: 12,
  paddingRight: 12,
  borderRadius: 4,
  paddingTop: 4,
  paddingBottom: 4,
  fontSize: 14,
  boxShadow: "none",
  borderColor: "#6366F1",
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const GreyLabelText = MUIStyled(SATypography)({
  color: "#6B7280",
  fontSize: "0.875rem",
  fontWeight: 500,
});

const Row = MUIStyled(SABox)({
  gap: 12,
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f9fafb",
  padding: "8px 12px",
  borderRadius: 8,
  marginBottom: 8,
});

const StyledLabel = MUIStyled(SATypography)({
  color: "#6B7280",
  fontSize: "0.875rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
});

const ProductMaterialsBox = MUIStyled(SABox)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  borderRadius: 4,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  fontSize: 14,
  boxShadow: "none",
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const ManufactureProcessingDetails = () => {
  const { t } = useI18N();
  const { api } = useAPI();
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [serialsOpen, setSerialsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);

  useEffect(() => {
    setLoading(true);
    api({
      method: httpMethods.get,
      endPoint: `${apiEndpoints.getInspectionRequests}${id}/`,
      needLoader: true,
      loader: loaderPath.processing,
      showToastOnError: true,
    }).then((res) => {
      if (res?.status) {
        setDetails(res?.data);
      }
      setLoading(false);
    });
  }, [id]);

  if (loading || !details) {
    return (
      <SABox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <SALoader />
      </SABox>
    );
  }

  const productQuantities =
    details?.manufacturing_request
      ?.manufacturing_product_requested_quantities || [];
  const products = productQuantities.map((item) => {
    const jewelryProduct = item.jewelry_product || {};
    const productMaterials = Array.isArray(jewelryProduct.product_materials)
      ? jewelryProduct.product_materials
      : [];
    const attachments = Array.isArray(
      jewelryProduct.jewelry_product_attachments,
    )
      ? jewelryProduct.jewelry_product_attachments
      : [];

    return {
      id: jewelryProduct.id ?? null,
      name: jewelryProduct.product_name ?? "-",
      type: jewelryProduct.product_type ?? "-",
      quantity: item.quantity ?? 0,
      weight: jewelryProduct.weight ?? "-",
      material_type: productMaterials[0]?.material_type ?? "-",
      metal_type:
        productMaterials.find((m) => m.material_type === "metal")
          ?.material_item ?? "-",
      design_images: attachments.map((att) => att?.url?.url).filter(Boolean),
      product_materials: productMaterials,
    };
  });

  const total_quantity = productQuantities.reduce(
    (sum, item) => sum + +(item?.quantity || 0),
    0,
  );

  const imageList = products.map((item) => ({
    url: item.design_images?.[0],
  }));
  const currentProduct = products[selectedIndex] || {};

  const {
    production_status,
    manufacturing_request,
    delivery_date,
    started_at,
    payment,
    ownership,
    completed_at,
    delivery_status,
    is_payment_completed,
    admin_inspection_status,
  } = details;

  function getProductionStatusLabel(status, t) {
    switch (status) {
      case "NOT_STARTED":
        return t("not-started");
      case "IN_PROGRESS":
        return t("in-progress");
      case "ON_HOLD":
        return t("on-hold");
      case "COMPLETED":
        return t("completed");
      case "WAITING_TO_SHIPMENT":
        return t("waiting-to-shipment");
      case "DELIVERED":
        return t("delivered");
      default:
        return t("not-started");
    }
  }

  const breadcrumbsData = [
    { label: "home", href: "/manufacturer" },
    { label: "processing", href: "/manufacturer?tab=in-progress" },
    { label: id, noTranslate: true },
  ];

  return (
    <>
      <SAStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <SAStack>
          <MUIBreadcrumbs breadcrumbsData={breadcrumbsData} />
        </SAStack>
      </SAStack>
      <SACard>
        <SABox sx={{ background: "#f5f5f5", p: 2, mb: 2, borderRadius: 2 }}>
          <SAStack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <SABox>
              <SATypography fontWeight={500}>
                {t("requested-quantity")}
              </SATypography>
              <SATypography>
                {+productQuantities[selectedIndex]?.quantity} {t("out-of")}{" "}
                {total_quantity} {t("pieces")}
              </SATypography>
            </SABox>
            <SATypography
              sx={{
                border: "1px solid rgb(4, 4, 4)",
                padding: "6px 16px",
                fontWeight: 500,
                fontSize: 18,
                background: "#f3f4f6",
                display: "inline-block",
              }}
            >
              {getProductionStatusLabel(production_status, t)}
            </SATypography>
          </SAStack>
        </SABox>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
            >
              {t("manufacture-details")}
            </SATypography>

            <DetailsRow>
              <GreyLabelText>
                {t("inspection-status")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SAStatusBadge status={admin_inspection_status} />
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <GreyLabelText>
                {t("payment-status")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SAStatusBadge
                  status={is_payment_completed ? "COMPLETED" : "PENDING"}
                />
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <GreyLabelText>
                {t("material-source")}
                {":"}
              </GreyLabelText>

              <SATypography>
                {manufacturing_request?.material_source?.toLowerCase()}
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <GreyLabelText>
                {t("delivery-status")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SAStatusBadge status={delivery_status} />
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <GreyLabelText>
                {t("delivery-date")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SADate date={delivery_date} />
              </SATypography>
            </DetailsRow>
          </Grid>

          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
              mt={2}
            >
              {t("timeline")}
            </SATypography>

            <DetailsRow>
              <GreyLabelText>
                {t("started-date")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SADate date={started_at} />
              </SATypography>
            </DetailsRow>

            <DetailsRow>
              <GreyLabelText>
                {t("completed-date")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SADate date={completed_at} />
              </SATypography>
            </DetailsRow>
          </Grid>

          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
              mt={2}
            >
              {t("participant-details")}
            </SATypography>

            <DetailsRow>
              <GreyLabelText>
                {t("jeweler")}
                {":"}
              </GreyLabelText>

              <SATruncateWithEllipsis
                text={
                  manufacturing_request?.business?.name ||
                  manufacturing_request?.created_by?.name
                }
                maxLength={35}
              />
            </DetailsRow>
            <DetailsRow>
              <GreyLabelText>
                {t("manufacturer")}
                {":"}
              </GreyLabelText>

              <SATruncateWithEllipsis
                text={
                  manufacturing_request?.manufacturing_estimation?.created_by
                    ?.name
                }
                maxLength={35}
              />
            </DetailsRow>
          </Grid>

          {payment && (
            <Grid item xs={12} md={6}>
              <SATypography
                variant="subtitle1"
                fontSize={16}
                fontWeight={700}
                gutterBottom
              >
                {t("payment-details")}
              </SATypography>
              <DetailsRow>
                <GreyLabelText>
                  {t("payment-type")}
                  {":"}
                </GreyLabelText>

                <SATypography>{payment?.payment_type}</SATypography>
              </DetailsRow>
              <DetailsRow>
                <GreyLabelText>
                  {t("total-amount")}
                  {":"}
                </GreyLabelText>

                <SATypography>
                  <SACost value={payment?.total_amount} compact={false} />
                </SATypography>
              </DetailsRow>

              <DetailsRow>
                <GreyLabelText>
                  {t("ownership")}
                  {":"}
                </GreyLabelText>

                <SATypography>
                  {t(
                    ownership === "INVESTOR"
                      ? "saver"
                      : ownership?.toLowerCase(),
                  )}
                </SATypography>
              </DetailsRow>

              {payment?.payment_type === "MUSHARAKAH" && (
                <Row
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSerialsOpen((prev) => !prev)}
                >
                  <StyledLabel variant="body2">{`${t("jeweler-selected-serial-number")}:`}</StyledLabel>
                  <ProductMaterialsBox>
                    <SATypography
                      sx={{
                        color: "#111827",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      {t(serialsOpen ? "hide" : "view")}
                    </SATypography>
                    <ExpandMoreIcon
                      style={{
                        transform: serialsOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </ProductMaterialsBox>
                </Row>
              )}
              <Collapse in={serialsOpen} timeout="auto" unmountOnExit>
                <SABox sx={{ mt: 1 }}>
                  <JewelerSerialNumberTable
                    data={payment?.asset_serial_numbers}
                    t={t}
                  />
                </SABox>
              </Collapse>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              gutterBottom
            >
              {t("estimation-details")}
            </SATypography>
            <DetailsRow>
              <GreyLabelText>
                {t("estimated-price")}
                {":"}
              </GreyLabelText>

              <SATypography>
                <SACost
                  value={
                    manufacturing_request?.manufacturing_estimation
                      ?.estimated_prices?.[0]?.estimated_price
                  }
                  compact={false}
                />
              </SATypography>
            </DetailsRow>
            <DetailsRow>
              <GreyLabelText>
                {t("duration")}
                {":"}
              </GreyLabelText>

              <SATypography>
                {manufacturing_request?.manufacturing_estimation?.duration}
              </SATypography>
            </DetailsRow>

            {manufacturing_request?.correction_amount && (
              <DetailsRow>
                <GreyLabelText>
                  {t("correction-amount")}
                  {":"}
                </GreyLabelText>

                <SATypography>
                  <SACost
                    value={manufacturing_request?.correction_amount}
                    compact={false}
                  />
                </SATypography>
              </DetailsRow>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SATypography
              variant="subtitle1"
              fontSize={16}
              fontWeight={700}
              color="primary.main"
              sx={{ mt: 2 }}
            >
              {t("product-details")}
            </SATypography>
          </Grid>
          <Grid item xs={12} md={4}>
            <SAImageSlider images={imageList} onChange={setSelectedIndex} />
          </Grid>

          <Grid item xs={12} md={8}>
            <DetailsRow>
              <GreyLabelText>
                {t("production-status")}
                {":"}
              </GreyLabelText>
              <SATypography>
                {(
                  <SAStatusBadge
                    status={productQuantities[selectedIndex]?.production_status}
                  />
                ) || "-"}
              </SATypography>
            </DetailsRow>
            <DetailsRow>
              <GreyLabelText>{t("product-details")}:</GreyLabelText>
              <SATypography
                variant="text"
                onClick={() => setDetailsModalOpen(true)}
                sx={{
                  minWidth: 0,
                  background: "#f3f4f6",
                  px: 1,
                  fontWeight: 500,
                  fontSize: 14,
                  py: 0.5,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {t("view-details")}
                <Eye />
              </SATypography>
            </DetailsRow>
            <DetailsRow
              style={{ cursor: "pointer" }}
              onClick={() => setMaterialsOpen((prev) => !prev)}
            >
              <GreyLabelText>{t("product-materials")}:</GreyLabelText>
              <DetailsToggle>
                <GreyLabelText>
                  {t(materialsOpen ? "hide" : "view")}
                </GreyLabelText>
                <ExpandMoreIcon
                  style={{
                    transform: materialsOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </DetailsToggle>
            </DetailsRow>
            <Collapse in={materialsOpen} timeout="auto" unmountOnExit>
              <SABox sx={{ mt: 0.5 }}>
                <ProductMaterialsTable product={currentProduct} t={t} />
              </SABox>
            </Collapse>
          </Grid>
        </Grid>
        {/* Product Details Modal */}
        <ManufactureProductDetailsModal
          open={detailsModalOpen}
          handleClose={() => setDetailsModalOpen(false)}
          t={t}
          product={products[selectedIndex]}
          productQuantity={productQuantities[selectedIndex]}
          setZoomImg={setZoomImg}
        />
        {/* Image Zoom Modal */}
        <MUIModal
          open={!!zoomImg}
          maxWidth={500}
          handleClose={() => setZoomImg(null)}
          content={
            zoomImg && (
              <SABox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <img
                  src={zoomImg}
                  alt="zoomed-design"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              </SABox>
            )
          }
        />
      </SACard>
    </>
  );
};

export default ManufactureProcessingDetails;
